import { useEffect } from 'react';
import { traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';
import type { MappingWorkspaceTabIndex } from '../lib/mappingWorkspacePageModel';

export interface UseQuickFillEmbeddedLayoutTraceArgs {
  activeTab: MappingWorkspaceTabIndex;
  focusRefs: MappingWorkspaceFocusRefs;
}

export interface UseQuickFillEmbeddedLayoutTraceResult {
  isQuickFillWorkspaceTab: boolean;
}

export function useQuickFillEmbeddedLayoutTrace({
  activeTab,
  focusRefs,
}: UseQuickFillEmbeddedLayoutTraceArgs): UseQuickFillEmbeddedLayoutTraceResult {
  const isQuickFillWorkspaceTab = activeTab === 1;

  useEffect(() => {
    if (!isQuickFillWorkspaceTab) {
      focusRefs.quickFillLayoutTraceKey.current = '';
      return undefined;
    }

    const shell = focusRefs.quickFillTabShell.current;
    if (!shell) {
      return undefined;
    }

    let animationFrameId: number | null = null;
    const measure = () => {
      animationFrameId = null;
      const quickFillSurface = shell.querySelector('[data-testid="quickfill-content-shell"]') as HTMLElement | null;
      const tableViewport = shell.querySelector('[data-testid="quick-fill-table-viewport"]') as HTMLElement | null;
      const payload = {
        activeTab,
        shellClientWidth: shell.clientWidth,
        shellScrollWidth: shell.scrollWidth,
        quickFillClientWidth: quickFillSurface?.clientWidth ?? null,
        quickFillScrollWidth: quickFillSurface?.scrollWidth ?? null,
        tableViewportClientWidth: tableViewport?.clientWidth ?? null,
        tableViewportScrollWidth: tableViewport?.scrollWidth ?? null,
      };
      const traceKey = JSON.stringify(payload);
      if (focusRefs.quickFillLayoutTraceKey.current === traceKey) {
        return;
      }

      focusRefs.quickFillLayoutTraceKey.current = traceKey;
      traceMappingFlow('quick fill embedded layout measured', payload);

      const shellOverflow = payload.shellScrollWidth > payload.shellClientWidth + 1;
      const quickFillOverflow =
        payload.quickFillClientWidth !== null &&
        payload.quickFillScrollWidth !== null &&
        payload.quickFillScrollWidth > payload.quickFillClientWidth + 1;
      if (shellOverflow || quickFillOverflow) {
        warnMappingFlow('quick fill embedded layout overflow', payload);
      }
    };
    const scheduleMeasure = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure);
    resizeObserver?.observe(shell);
    window.addEventListener('resize', scheduleMeasure, { passive: true });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [activeTab, focusRefs, isQuickFillWorkspaceTab]);

  return { isQuickFillWorkspaceTab };
}
